import { Flex } from 'rebass/styled-components';
import React, { forwardRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import DataTable from 'components/molecules/agGrid';
import { convertQueryToString } from 'helpers/queryString';
import { trackEvent } from 'helpers/analytics';
import { Funnel } from 'lucide-react';
import { ToolbarButton } from 'components/molecules/agGrid/components/toolbarButton';
import useJobActions from '../../../hooks/useJobActions';
import { activeNetworkSelector, jobApplicantsJobTnSelector, userSelector } from '../../../redux/selectors';
import { isNetworkAdminSelector } from '../../../hooks/usePermissions/selectors';
import { selectAllJobsSelector, selectedJobsSelector } from '../../../hooks/useJobs/selectors';
import { getJobsColumns } from './jobColumns';
import { useTableRows } from './hooks/useTableRows';
import { useActionItems } from './hooks/useActionItems';
import { useJobTableFunctions } from './hooks/useJobTableFunctions';
import { ExportJobs } from './exportJobs';
import { JobTableModals } from './jobTableModals';
import { JobsSidebar } from '../jobsTableWithFilters/jobsSidebar';
import UpgradeModal from '../upgradeModal';
import NoProjectAccessModal from '../noProjectAccessModal';

export const JobsDataTable = forwardRef(
  (
    {
      isJobsLoading,
      isJobsInitialized,
      totalJobs,
      loadNextPage,
      history,
      jobs,
      hasMoreJobs,
      onSort,
      emptyView,
      initialSortBy,
      source,
      isCompanyProfile,
      onFiltersClicked,
      jobFilterProps,
      showSkeleton,
    },
    ref,
  ) => {
    const { jobActions, jobActionsLoading, jobActionsErrors, dialogStates } = useJobActions({
      source,
    });
    const [selectedJob, setSelectedJob] = useState(null);
    const location = useLocation();
    const activeNetwork = useSelector(activeNetworkSelector);
    const isNetworkAdmin = useSelector(isNetworkAdminSelector);
    const companyItemsToHide = useMemo(() => {
      const columnsToHide = isCompanyProfile ? ['visibility', 'featured'] : [];
      return columnsToHide;
    }, [isCompanyProfile]);
    const jobsColumns = getJobsColumns(new Set(companyItemsToHide), {
      totalJobs,
      isLoading: isJobsLoading && !isJobsInitialized,
    });
    const {
      onFindCandidates,
      onOpenProject,
      getJobById,
      onSelect,
      isJobSourcePostedActiveOrExpired,
      handleSort,
      onColumnAction,
      onShowJobRulesClick,
      showJobRules,
      setShowJobRules,
      showUpgradeModal,
      upgradePaywallOrigin,
      dismissUpgradeModal,
      showNoAccessModal,
      dismissNoAccessModal,
    } = useJobTableFunctions({ jobs, onSort });
    const showApplicants = useSelector(jobApplicantsJobTnSelector) && activeNetwork?.connect?.hasAccess;
    const user = useSelector(userSelector);

    const tableRows = useTableRows({
      userEmail: user?.email,
      jobs,
      onShowJobRulesClick,
      selectedJobId: selectedJob?.id,
      onFindCandidates,
      onOpenProject,
      onApplicantsClicked: (job) => {
        trackEvent('jobs:see_applicants', { jobId: job.id, numberApplicants: job.jobApplicantsCount });

        history.push(
          `/networks/${activeNetwork.slug}/contacts?${convertQueryToString({
            filters: { jobApplicationsJobs: [{ value: job.id, label: job.title }] },
          })}`,
        );
      },
      showApplicants,
      hasConnect: activeNetwork?.connect?.hasAccess,
      showSkeleton,
    });

    const selectedJobs = useMemo(() => jobs.filter((job) => job.selected).map((job) => job.id), [jobs]);
    const reduxSelectedJobs = useSelector(selectedJobsSelector);
    const selectAllJobs = useSelector(selectAllJobsSelector);
    const hasBulkActions = selectAllJobs || reduxSelectedJobs.length > 0;

    const actionMenuItems = useActionItems({
      jobActionsLoading,
      isJobSourcePostedActiveOrExpired,
      getJobById,
      jobActions,
      activeNetwork,
      source,
      isNetworkAdmin,
      setSelectedJob,
      isCompanyProfile,
      showApplicants,
      onFindCandidates,
      onOpenProject,
    });

    const tableHeight = useMemo(
      () => (source === 'employer_profile' ? 'calc(100vh - 200px)' : 'calc(100vh - 100px)'),
      [source],
    );

    return (
      <Flex flexDirection="column" flexGrow="1" sx={{ zIndex: 1, position: 'relative' }}>
        <DataTable
          ref={ref}
          rightActions={
            <ExportJobs
              totalJobs={totalJobs}
              selectedJobs={selectedJobs}
              selectAllJobs={selectAllJobs}
              company={jobFilterProps?.company}
            />
          }
          leftActions={
            <ToolbarButton
              option={{ value: 'filter', label: 'Filter', icon: Funnel }}
              toolbarOptionChange={onFiltersClicked}
              active={Boolean(jobFilterProps?.showFilters)}
              toolbarCount={jobFilterProps?.filtersCount || 0}
            />
          }
          useHorizontalActions={false}
          fixedColumn="first"
          totalItems={totalJobs}
          canSort
          canSelect={isNetworkAdmin}
          onSelect={onSelect}
          selectedAll={selectAllJobs}
          initialSortBy={initialSortBy}
          onSort={handleSort}
          loadMore={loadNextPage}
          items={tableRows}
          columnDefinition={jobsColumns}
          hasMore={hasMoreJobs}
          isInitialized={isJobsInitialized || showSkeleton}
          isLoadingMore={isJobsInitialized && isJobsLoading && !showSkeleton}
          emptyView={emptyView}
          onColumnAction={onColumnAction}
          persistColumnKey={`${activeNetwork.id}-${source}-${isCompanyProfile ? 'company-profile' : 'companies'}-v7`}
          gridOptions={{
            suppressRowClickSelection: false,
            cellSelection: true,
            animateRows: false,
            processCellForClipboard: (params) => params.formatValue(),
            rowClassRules: {},
          }}
          rowActions={showSkeleton ? { height: 330, items: [] } : { height: 330, items: actionMenuItems }}
          sx={{
            height: tableHeight,
          }}
          filterSx={{
            maxHeight: tableHeight,
          }}
          cellStyle={{
            padding: '12px 8px',
          }}
          FiltersComponent={JobsSidebar}
          companyFilters={jobFilterProps}
          hasBulkActions={hasBulkActions}
        />
        <JobTableModals
          dialogStates={dialogStates}
          selectedJob={selectedJob}
          jobActions={jobActions}
          jobActionsLoading={jobActionsLoading}
          jobActionsErrors={jobActionsErrors}
          isNetworkAdmin={isNetworkAdmin}
          activeNetwork={activeNetwork}
          location={location}
          showJobRules={showJobRules}
          setShowJobRules={setShowJobRules}
        />
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={dismissUpgradeModal}
          featureName="people_finder"
          paywallOrigin={upgradePaywallOrigin}
        />
        <NoProjectAccessModal isOpen={showNoAccessModal} onClose={dismissNoAccessModal} />
      </Flex>
    );
  },
);

JobsDataTable.propTypes = {
  isJobsLoading: PropTypes.bool,
  isJobsInitialized: PropTypes.bool,
  totalJobs: PropTypes.number,
  loadNextPage: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  jobs: PropTypes.array,
  hasMoreJobs: PropTypes.bool,
  onSort: PropTypes.func,
  emptyView: PropTypes.node,
  initialSortBy: PropTypes.shape({
    id: PropTypes.string.isRequired,
    desc: PropTypes.bool.isRequired,
  }),
  source: PropTypes.string,
  isCompanyProfile: PropTypes.bool,
  onFiltersClicked: PropTypes.func,
  jobFilterProps: PropTypes.object,
  showSkeleton: PropTypes.bool,
};

JobsDataTable.defaultProps = {
  isJobsLoading: false,
  isJobsInitialized: false,
  totalJobs: 0,
  jobs: [],
  hasMoreJobs: false,
  onSort: () => {},
  emptyView: null,
  initialSortBy: { id: 'expirationDate', desc: false },
  source: 'jobs',
  isCompanyProfile: false,
  onFiltersClicked: () => {},
  jobFilterProps: {},
  showSkeleton: false,
};
